"use client";

import { AspectRatio, VideoPlayerProps } from "@12-week/lib/types";
import { Stream } from "@cloudflare/stream-react";
import { generateCloudflareStreamThumbnailUrl } from "@packages/lib/utils";
import Image from "next/image";
import React from "react";

export default function VideoPlayer({
  video,
  currentTime,
  streamRef,
  aspectRatio = AspectRatio.Landscape,
  intrinsicWidth,
  intrinsicHeight,
}: VideoPlayerProps) {
  const poster =
    video.thumbnail ||
    generateCloudflareStreamThumbnailUrl(
      video.videoId,
      video.thumbnailTimestampSeconds ?? 6,
    );
  const title = video.title || "Untitled Video";

  // Aspect ratio class
  let aspectRatioClass = "aspect-video";
  switch (aspectRatio) {
    case "instagram":
      aspectRatioClass = "aspect-4/5";
      break;
    case "portrait":
      aspectRatioClass = "aspect-9/16";
      break;
    case "landscape":
    default:
      aspectRatioClass = "aspect-video";
      break;
  }

  // Image dimensions and sizes based on aspect ratio
  let imgWidth = 1920;
  let imgHeight = 1080;
  let imgSizes = "(min-width: 1280px) 1280px, 100vw";

  switch (aspectRatio) {
    case "instagram":
      imgWidth = intrinsicWidth ?? 1080;
      imgHeight = intrinsicHeight ?? 1350;
      imgSizes = "(min-width: 1280px) 208px, 100vw";
      break;
    case "portrait":
      imgWidth = intrinsicWidth ?? 1080;
      imgHeight = intrinsicHeight ?? 1920;
      imgSizes = "(min-width: 1280px) 720px, 100vw";
      break;
    case "landscape":
    default:
      imgWidth = intrinsicWidth ?? 1920;
      imgHeight = intrinsicHeight ?? 1080;
      imgSizes = "(min-width: 1280px) 1280px, 100vw";
      break;
  }

  // Stream props for aspect ratios
  let streamProps: Partial<React.ComponentProps<typeof Stream>> = {};
  switch (aspectRatio) {
    case "instagram":
      streamProps = {
        height: "260px",
        width: "208px",
        responsive: false,
      };
      break;
    case "portrait":
      streamProps = {
        height: "480px",
        width: "270px",
        responsive: false,
      };
      break;
    // landscape/default: no extra props
  }

  return (
    <div
      className={`bg-secondary not-prose relative min-h-full w-full items-center ${aspectRatioClass}`}
      role="region"
      aria-label="Video player"
    >
      <Stream
        controls
        className="absolute inset-0 z-10 h-full w-full border-none"
        src={video.videoId}
        title={title}
        poster={poster}
        aria-label={`Video player for ${title}`}
        currentTime={currentTime}
        streamRef={streamRef}
        {...streamProps}
      />
      <Image
        src={poster}
        alt={title}
        className={`absolute inset-0 z-1 h-full w-full object-cover opacity-80 ${aspectRatioClass}`}
        height={imgHeight}
        width={imgWidth}
        sizes={imgSizes}
      />
    </div>
  );
}
